"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTenantLandingEnabled, useTenantStoreAsHome } from "@/components/tenant/TenantProvider";
import { api } from "@/lib/api-client";
import LandingRenderer from "@/components/landing/LandingRenderer";
import { EMPTY_LANDING_DATA, type LandingData } from "@/components/landing/types";
import StorePageClient from "@/components/store/StorePageClient";
import { Skeleton } from "@/components/ui/Skeleton";

// Halaman `/` tenant.
// - landing_enabled false/undefined (dari window.__TENANT__): redirect /login —
//   persis perilaku lama, kompatibel mundur penuh.
// - true: fetch /api/public/landing → render LandingRenderer.
// Gagal fetch / config kosong → tetap redirect /login (fallback aman).

// API mengembalikan bentuk flat {enabled, sections, data}; bentuk bersarang
// {config:{...}, data} diterima juga demi kompatibilitas.
interface LandingResponse {
  enabled?: boolean;
  sections?: unknown;
  config?: { enabled?: boolean; sections?: unknown };
  data?: LandingData;
}

/**
 * Initial state dari window.__TENANT_HOME__ (disuntik edge, lihat
 * resolveTenantHomeData di apps/web/functions/_lib/tenant.ts) — menghindari
 * HTML awal '/' kosong (skeleton) sampai fetch client di useEffect di bawah
 * selesai. Fetch itu TETAP jalan sebagai revalidasi diam-diam, bukan dihapus.
 */
function landingFromHomeData(): { sections: unknown; data: LandingData } | null {
  if (typeof window === "undefined") return null;
  const home = window.__TENANT_HOME__;
  if (!home || home.path !== "/api/public/landing") return null;
  const res = home.body as { success?: boolean; data?: LandingResponse } | undefined;
  if (!res?.success || !res.data) return null;
  const cfg = res.data.config ?? res.data;
  if (!cfg?.enabled) return null;
  return { sections: cfg.sections, data: res.data.data ?? EMPTY_LANDING_DATA };
}

function LandingSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-black/[0.06]">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <Skeleton className="h-8 w-36 rounded-lg" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-20 rounded-lg" />
            <Skeleton className="h-9 w-20 rounded-lg" />
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 py-20">
        <Skeleton className="h-12 w-3/4 max-w-xl rounded-xl" />
        <Skeleton className="mt-4 h-5 w-1/2 max-w-md rounded" />
        <div className="mt-8 flex gap-3">
          <Skeleton className="h-12 w-40 rounded-xl" />
          <Skeleton className="h-12 w-36 rounded-xl" />
        </div>
        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-56 rounded-2xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function RootPage() {
  const router = useRouter();
  const landingEnabled = useTenantLandingEnabled();
  const storeAsHome = useTenantStoreAsHome();
  const [landing, setLanding] = useState<{ sections: unknown; data: LandingData } | null>(landingFromHomeData);

  useEffect(() => {
    // store_as_home ditangani di render (StorePageClient) — jangan fetch landing.
    if (storeAsHome) return;
    if (!landingEnabled) {
      router.replace("/login");
      return;
    }

    let cancelled = false;
    api<LandingResponse>("/api/public/landing").then((res) => {
      if (cancelled) return;
      const cfg = res.data?.config ?? res.data;
      if (res.success && cfg?.enabled) {
        setLanding({
          sections: cfg.sections,
          data: res.data?.data ?? EMPTY_LANDING_DATA,
        });
      } else {
        // API belum siap / landing dimatikan di sisi server → perilaku lama
        router.replace("/login");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [landingEnabled, storeAsHome, router]);

  // Etalase sebagai beranda: render store di '/' (fallback /login bila nonaktif).
  if (storeAsHome) return <StorePageClient redirectWhenDisabled />;

  if (!landingEnabled || !landing) return <LandingSkeleton />;

  return <LandingRenderer sections={landing.sections} data={landing.data} />;
}
